// OTP(인증번호) 입력 화면.
// /auth/sms/verify 호출 후 verificationToken 을 약관 동의 화면으로 전달한다.
// 1-G 에서 verificationToken + 약관 동의 결과로 /auth/signup-or-login (가칭) 을 호출하면서 본 흐름이 완성된다.

import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { verifyOtp, resolveLogin } from "@/features/auth/authApi.ts";
import { ApiError } from "@/api/client.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { Button } from "@/shared/ui";

// 소셜 콜백에서 시작된 흐름이면 link 정보가 함께 흘러온다(없으면 순수 휴대폰 가입).
interface SocialLinkState {
  provider: "kakao" | "naver" | "google";
  linkToken: string;
}

interface LocationState {
  phone?: string;
  // 앞 화면(/login/phone)에서 입력한 이름. 신규 가입 시 회원 이름으로 저장.
  displayName?: string;
  expiresInSec?: number;
  // dev 환경 편의용 - 운영 빌드에서는 undefined.
  devCode?: string;
  socialLink?: SocialLinkState | null;
}

// OTP 입력 화면 컴포넌트.
// location.state.phone 이 없으면 /login 으로 다시 보낸다 (직접 URL 진입 차단).
export default function OtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as LocationState | null;
  const phone = state?.phone ?? "";
  const displayName = state?.displayName ?? "";
  const devCode = state?.devCode;
  const socialLink = state?.socialLink ?? null;

  const setSession = useAuthStore((s) => s.setSession);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!phone) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/u.test(code)) {
      setError("6자리 숫자 인증번호를 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await verifyOtp(phone, code);
      // 기존 회원이면 약관 재동의 없이 바로 로그인한다(휴대폰 재로그인 / 소셜-휴대폰 계정통합).
      // 신규 회원만 약관 동의 화면으로 보낸다.
      const resolved = await resolveLogin({
        verificationToken: result.verificationToken,
        linkToken: socialLink?.linkToken,
      });
      if (resolved.status === "login") {
        setSession({
          accessToken: resolved.accessToken,
          refreshToken: resolved.refreshToken,
          expiresAtSec: resolved.expiresAtSec,
          user: {
            id: resolved.user.id,
            displayName: resolved.user.displayName,
            tier: resolved.user.tier,
          },
        });
        navigate("/", { replace: true });
        return;
      }
      // 신규: 앞 화면에서 받은 이름을 회원 이름으로 함께 넘긴다. 소셜이면 linkToken + verificationToken 도 함께 제출.
      navigate("/login/consents", {
        state: socialLink
          ? {
              source: "social",
              provider: socialLink.provider,
              linkToken: socialLink.linkToken,
              verificationToken: result.verificationToken,
              phone,
              displayName,
            }
          : {
              source: "phone",
              phone,
              verificationToken: result.verificationToken,
              displayName,
            },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // 휴대폰 번호 마스킹 (가운데 자리 일부 가림). PII 노출 최소화 + 사용자 확인 정보 동시 제공.
  const masked = phone.replace(/^(\d{3})(\d{3,4})(\d{4})$/u, (_m, a: string, b: string, c: string) => {
    const midMasked = b.slice(0, 1) + "*".repeat(Math.max(0, b.length - 2)) + b.slice(-1);
    return `${a}-${midMasked}-${c}`;
  });

  return (
    <main className="mx-auto flex min-h-dvh max-w-screen-sm flex-col gap-8 px-6 py-10">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">인증번호 입력</h1>
        <p className="text-base text-ink-600">{masked} 로 전송된 6자리 숫자를 입력해 주세요.</p>
        {devCode ? (
          <p className="text-sm text-warning-700">
            (개발용 안내) 이번 코드: {devCode}
          </p>
        ) : null}
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        <label htmlFor="code" className="text-base font-medium">
          인증번호
        </label>
        <input
          id="code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/gu, ""))}
          aria-invalid={error !== null}
          aria-describedby={error ? "code-error" : undefined}
          className="h-12 rounded-md border border-ink-300 px-3 text-center text-xl tracking-widest"
        />
        {error ? (
          <p id="code-error" className="text-sm text-danger-700">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          fullWidth
          disabled={code.length !== 6}
          loading={submitting}
        >
          {submitting ? "확인 중..." : "확인"}
        </Button>
      </form>
    </main>
  );
}
